import { BuildSteps, DeploySteps, Step } from "@/types/flows";

export enum AnalyticsFlowEvent {
    // Page Views
    PAGE_VIEW = "Page View",
    BACK = "Back",
    // Home Page
    FEEDBACK_OPENED = "Feedback Opened",
    FEEDBACK_SUBMITTED = "Feedback Submitted",
    PROJECT_IDEA_SUBMITTED = "Project Idea Submitted",
    TASK_SELECTED = "Task Selected",
    DISABLED_TASK_CLICKED = "Disabled Task Clicked",
    // Upload
    FILE_UPLOADER_CLICKED = "File Uploader Clicked",
    FILE_SELECT_CLICKED = "File Select Clicked",
    FILE_INPUT_CLICKED = "File Input Clicked",
    CAMERA_INPUT_CLICKED = "Camera Input Clicked",
    QR_UPLOAD_BUTTON_CLICKED = "QR Upload Button Clicked",
    EXAMPLE_VIDEO_CLICKED = "Example Video Clicked",
    EXAMPLE_VIDEO_USED = "Example Video Used",
    TRIM_STARTED = "Trim Started",
    TRIM_CANCELED = "Trim Canceled",
    UPLOAD_CANCELED = "Upload Canceled",
    FILE_UPLOADED = "File Uploaded",
    FIND_MY_OBJECTS_CLICKED = "Find My Objects Clicked",
    EXTRACTED_OBJECTS_FROM_PROMPT = "Extracted Objects From Prompt",
    // Prompt
    BUILD_MODEL_CLICKED = "Build Model Clicked",
    ANNOTATION_BOX_DRAWN = "Annotation Box Drawn",
    GENERATE_PREDICTIONS_BUTTON_CLICKED = "Prompt Box Submitted",
    APPROVE_BUTTON_CLICKED = "Annotation Approve Clicked",
    REJECT_BUTTON_CLICKED = "Annotation Reject Clicked",
    FILE_NAVIGATION_CLICKED = "File Navigation Clicked",
    ANNOTATION_OBJECT_DELETED = "Annotation Box Deleted",
    PREDICTION_OBJECT_DELETED = "Prediction Box Deleted",
    SMART_CHIP_CLICKED = "Prompt Suggestion Clicked",
    NEW_OBJECT_ADDED = "Annotation Class Drafted",
    TOGGLE_TEXT_PROMPT_MODE = "Text Mode Toggle Clicked",
    TOGGLE_TEXT_PROMPT_MODE_MODAL_OPENED = "Text Mode Modal Opened",
    // Review / Preview
    REPROMPT_CLICKED = "Reprompt Clicked",
    TEST_MODEL_CLICKED = "Test Model Clicked",
    MODEL_TESTED = "Model Tested",
    DOWNLOAD_ANNOTATED_CLICKED = "Download Annotated Clicked",
    DOWNLOADED_ANNOTATED_FILE = "Downloaded Annotated File",
    GET_API_KEY_CLICKED = "Get API Key Clicked",
    CREATE_WORKFLOW_STARTED = "Create Workflow Started",
    WORKFLOW_CREATED = "Workflow Created",

    // Deploy
    DATA_MODE_SELECTED = "Data Mode Selected",
    CODE_SNIPPET_COPIED = "Code Snippet Copied",
    MARKDOWN_COPY_CLICKED = "Markdown Copy Clicked",
    DOCS_BUTTON_CLICKED = "Docs Button Clicked",
    CHATGPT_BUTTON_CLICKED = "ChatGPT Button Clicked",
    CLAUDE_BUTTON_CLICKED = "Claude Button Clicked",
    LOVABLE_BUTTON_CLICKED = "Lovable Button Clicked",
    OPEN_WORKFLOWS_BUTTON_CLICKED = "Open Workflows Button Clicked"
}

export enum AnalyticsAuthenticationEvent {
    USER_SIGNED_IN = "User Signed In",
    USER_SIGNED_OUT = "User Signed Out"
}

export enum AnalyticsPageView {
    HOME = "Home",
    UPLOAD = "Upload",
    PROMPT = "Prompt",
    REVIEW = "Review",
    PREVIEW = "Preview",
    DEPLOY = "Deploy"
}

export const StepToAnalyticsPageViewMap: Record<
    Exclude<Step, null | undefined>,
    AnalyticsPageView
> = {
    [BuildSteps.UPLOAD]: AnalyticsPageView.UPLOAD,
    [BuildSteps.PROMPT]: AnalyticsPageView.PROMPT,
    [BuildSteps.REVIEW]: AnalyticsPageView.REVIEW,
    [BuildSteps.PREVIEW]: AnalyticsPageView.PREVIEW,
    [DeploySteps.DEPLOY]: AnalyticsPageView.DEPLOY
};

export type AnalyticsEvent = AnalyticsFlowEvent | AnalyticsAuthenticationEvent;

export enum AppSource {
    RAPID = "rapid"
}

export type AnalyticsEventData = Record<string, any> & {
    appSource: AppSource;
    source?: "canvas" | "thumbnail";
};

export type AnalyticsEventPayload = { type: AnalyticsEvent; data: AnalyticsEventData };

export enum AnalyticsMethod {
    TRACK = "track",
    IDENTIFY = "identify",
    PAGE = "page"
}

export type AnalyticsUserIdentity = {
    id: string;
    email: string;
    name: string;
    displayName: string;
    photo: string;
    method: string;
};

export type AnalyticsWorkspaceIdentity = {
    workspaceId: string;
    workspaceUrl: string;
    workspaceName: string;
    workspacePlan: string;
};

export type AnalyticsWorkflowIdentity = {
    workflowId: string;
    workflowUrl: string;
    workflowName: string;
};

export type AnalyticsIdentity = AnalyticsUserIdentity &
    AnalyticsWorkspaceIdentity &
    AnalyticsWorkflowIdentity;
